import { FC, useState } from 'react';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { List } from '@ravago/shared/radiance/components/list/components/List/List';
import { ListItem } from '@ravago/shared/radiance/components/list/components/ListItem/ListItem';
import { Menu } from '@ravago/shared/radiance/components/menu/components/Menu/Menu';
import { useTranslations } from '@ravago/shared/translations/client';

import { SystemPageLink } from '../../content/system-page-link/system-page-link';

export const OrderLineTableMenu: FC<{
   orderLine: DisplayOrder;
   locale: string;
   orderBaseUrls: OrderBaseUrls;
   openedFrom: OrderLineOpenedFrom;
   shipmentId?: number;
   onOrderLineButtonClick: (orderLine: DisplayOrder) => void;
}> = ({ orderLine, locale, openedFrom, shipmentId, orderBaseUrls, onOrderLineButtonClick }) => {
   const translate = useTranslations();
   const [menuOpen, setMenuOpen] = useState(false);

   const handleOrderLineButtonClick = () => {
      setMenuOpen(false);
      onOrderLineButtonClick(orderLine);
   };

   return (
      <Box justify="end">
         <Menu
            open={menuOpen}
            align="right"
            width="fill"
            toggleWidth="full"
            closeOnOutsideClick
            onMenuClose={() => setMenuOpen(false)}
            content={
               <List>
                  <ListItem onClick={handleOrderLineButtonClick}>
                     {orderLine.isCallOff ? translate('shipment.table.request-delivery') : translate('shipment.table.show-more')}
                  </ListItem>
                  {!orderLine.isCallOff && openedFrom !== 'order_detail' && (
                     <SystemPageLink
                        baseRoute={orderBaseUrls.orderDetailBaseUrl}
                        locale={locale}
                        type={SystemPageType.ORDER_DETAIL}
                        slug={orderLine.orderNumber.slice(0, orderLine.orderNumber.indexOf('-'))}
                     >
                        <ListItem>{translate('shipment.table.order-details')}</ListItem>
                     </SystemPageLink>
                  )}
                  {(orderLine.agreementNumber !== undefined || orderLine.isCallOff) && (
                     <SystemPageLink
                        baseRoute={orderBaseUrls.callOffAgreementBaseUrl}
                        locale={locale}
                        type={SystemPageType.CALL_OFF_ORDER_DETAIL}
                        slug={orderLine.isCallOff ? orderLine.orderNumber : orderLine.agreementNumber}
                     >
                        <ListItem>{translate('shipment.table.call-off-agreement')}</ListItem>
                     </SystemPageLink>
                  )}
                  {shipmentId ? (
                     <SystemPageLink
                        baseRoute={orderBaseUrls.shipmentBaseUrl}
                        locale={locale}
                        type={SystemPageType.SHIPMENT}
                        slug={shipmentId.toString()}
                     >
                        <ListItem>{translate('shipment.table.shipment-details')}</ListItem>
                     </SystemPageLink>
                  ) : (
                     orderLine.shipmentIds &&
                     orderLine.shipmentIds.length > 0 &&
                     orderLine.shipmentIds.map((id) => (
                        <SystemPageLink
                           key={id}
                           baseRoute={orderBaseUrls.shipmentBaseUrl}
                           locale={locale}
                           type={SystemPageType.SHIPMENT}
                           slug={id.toString()}
                        >
                           <ListItem>{`${translate('shipment.table.shipment-details')} ${id}`}</ListItem>
                        </SystemPageLink>
                     ))
                  )}
                  <SystemPageLink baseRoute={orderBaseUrls.myProductsAndPricesBaseUrl} type={SystemPageType.MY_PRODUCTS_AND_PRICES} locale={locale}>
                     <ListItem>{translate('shipment.products.new-quote')}</ListItem>
                  </SystemPageLink>
               </List>
            }
         >
            <Box content="end" items="end">
               <IconButton
                  icon="ellipsis-vertical"
                  ariaLabel={`More options ${orderLine.orderNumber}`}
                  onClick={() => setMenuOpen((curr) => !curr)}
                  type="button"
                  variant="on-light"
               />
            </Box>
         </Menu>
      </Box>
   );
};
